import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FolderIcon from '@mui/icons-material/Folder';
import { useTestGroupParentTypesGet } from '../../../clients/graphql/pages/test-groups-queries';

// Format enum values nicely: AUM_ROLLOUT -> Aum Rollout
const formatEnumValue = (value: string): string => {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const TestGroupsPage = () => {
  const navigate = useNavigate();
  const { loading, error, data } = useTestGroupParentTypesGet({});
  const parentTypes: string[] = data?.testGroupParentTypesGet?.parentTypes || [];

  const handleParentTypeClick = (parentGroupId: string) => {
    navigate(`/developer/test-groups/${parentGroupId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching test group parent types. Please reload the page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom textAlign="center" sx={{ mb: 4 }}>
        Test Groups
      </Typography>

      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
        Select a parent type to view its test groups
      </Typography>

      {parentTypes.length === 0 ? (
        <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
          No test group parent types found.
        </Alert>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          {parentTypes.map((parentType) => (
            <Grid item xs={12} sm={6} md={4} key={parentType}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handleParentTypeClick(parentType)}
                  sx={{ height: '100%', p: 2 }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <FolderIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" component="div">
                      {formatEnumValue(parentType)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
